/**
 * Inline SVG icons.
 *
 * Hand-written rather than an icon package: there are eight of them, they are a few hundred bytes
 * total, and a dependency would pull in a tree-shaking question and a licence for something this
 * small. `currentColor` throughout so they inherit the accent on active nav items without any
 * per-icon styling.
 */

interface IconProps {
  size?: number;
}

function svg(path: JSX.Element, size: number): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

export const IconUsers = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16 4.8a3.5 3.5 0 0 1 0 6.4" />
      <path d="M18 14.4c2 .8 3.5 2.6 3.5 5.6" />
    </>,
    size,
  );

export const IconHome =({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </>,
    size,
  );

export const IconList = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </>,
    size,
  );

export const IconMic = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </>,
    size,
  );

export const IconChat = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M4 5.5h16v10H12l-4.5 4v-4H4z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>,
    size,
  );

export const IconShield = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M12 2.8 20 6v6c0 4.5-3.2 7.9-8 9.2-4.8-1.3-8-4.7-8-9.2V6z" />
      <path d="m9.2 12 2 2 3.6-3.8" />
    </>,
    size,
  );

export const IconBriefcase = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </>,
    size,
  );

export const IconDownload = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M4 19h16" />
    </>,
    size,
  );

export const IconLock = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <rect x="5" y="11" width="14" height="10" rx="1" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>,
    size,
  );

export const IconMap = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20z" />
      <path d="M9 4v13.5" />
      <path d="M15 6.5V20" />
    </>,
    size,
  );

export const IconGear = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
      <path d="M1.5 14h5M9.5 8h5M17.5 16h5" />
    </>,
    size,
  );

export const IconCube = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M12 2.6 20.5 7v10L12 21.4 3.5 17V7z" />
      <path d="M3.5 7 12 11.5 20.5 7" />
      <path d="M12 11.5V21.4" />
    </>,
    size,
  );

/** A spawner cage: a box with bars. */
export const IconCage = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="1.5" />
      <path d="M9.2 3.5v17M14.8 3.5v17M3.5 9.2h17M3.5 14.8h17" />
    </>,
    size,
  );

/** Two crossed swords, for the wager (duel) debug page. */
export const IconSwords = ({ size = 16 }: IconProps): JSX.Element =>
  svg(
    <>
      <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
      <path d="m13 19 6-6M16 16l4 4M19 21l2-2" />
      <path d="M9.5 6.5 18 3h3v3l-3.5 8.5" />
      <path d="m5 14 4 4M7 17l-3 3M3 19l2 2" />
    </>,
    size,
  );

export const IconMenu =({ size = 16 }: IconProps): JSX.Element =>
  svg(<path d="M3.5 6h17M3.5 12h17M3.5 18h17" />, size);

export const IconClose = ({ size = 16 }: IconProps): JSX.Element =>
  svg(<path d="m6 6 12 12M18 6 6 18" />, size);

export const IconChevron = ({ size = 14 }: IconProps): JSX.Element =>
  svg(<path d="m6 9 6 6 6-6" />, size);
